import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../../utils/languageUtils';
import './StickerTabThemeSelector.css';

const StickerTabThemeSelector = ({
  themes = [],
  selectedTheme,
  onThemeSelect,
  onStickerSetSelect,
  allowThemeSelection = true // Controls whether to show theme carousel or not
}) => {
  const { t, currentLang } = useTranslation();

  const stickerSetsContainerRef = useRef(null);

  const [themePageIndex, setThemePageIndex] = useState(0);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [selectedStickerSet, setSelectedStickerSet] = useState(null);
  const [localSelectedTheme, setLocalSelectedTheme] = useState(selectedTheme);

  const THEMES_PER_PAGE = 3;

  const getThemesWithContent = useCallback(() => {
    if (!themes || !Array.isArray(themes)) return [];
    return themes.filter(theme => {
      const hasStickers = Array.isArray(theme.StickersCsv) && theme.StickersCsv.length > 0;
      const hasBackgrounds = Array.isArray(theme.BackgroundsCsv) && theme.BackgroundsCsv.length > 0;
      return hasStickers || hasBackgrounds;
    });
  }, [themes]);

  const displayThemes = getThemesWithContent();

  const getCurrentPageThemes = () => {
    return displayThemes.slice(themePageIndex * THEMES_PER_PAGE, (themePageIndex + 1) * THEMES_PER_PAGE);
  };

  const getTotalPages = () => Math.ceil(displayThemes.length / THEMES_PER_PAGE);

  const handleThemeSelect = (theme) => {
    if (!theme) return;

    const idx = displayThemes.findIndex(t =>
      (t.ThemeName === theme.ThemeName) || (t.id && theme.id && t.id === theme.id)
    );

    if (idx !== -1) {
      setThemePageIndex(Math.floor(idx / THEMES_PER_PAGE));
    }

    let allSets = [];

    if (theme.StickersCsv?.length > 0) {
      allSets = allSets.concat(theme.StickersCsv.map(csv => ({
        StickerName: csv.Name,
        StickerUrl: csv.Url,
        stickerSet: { Name: csv.Name, Url: csv.Url },
        isColorSet: false,
        isBackground: false
      })));
    }

    if (theme.BackgroundsCsv?.length > 0) {
      allSets = allSets.concat(theme.BackgroundsCsv.map(csv => ({
        StickerName: csv.Name,
        StickerUrl: csv.Url,
        stickerSet: { Name: csv.Name, Url: csv.Url },
        isColorSet: false,
        isBackground: true
      })));
    }

    if (allSets.length > 0) {
      const firstSet = allSets[0];
      setSelectedStickerSet(firstSet);

      const themeWithSets = {
        ...theme,
        stickerSets: allSets,
        selectedSticker: firstSet
      };

      setLocalSelectedTheme(themeWithSets);

      if (onThemeSelect) onThemeSelect(themeWithSets);
      if (onStickerSetSelect && firstSet) setTimeout(() => onStickerSetSelect(firstSet), 100);
    } else {
      const emptyTheme = { ...theme, stickerSets: [] };
      setLocalSelectedTheme(emptyTheme);
      setSelectedStickerSet(null);
      if (onThemeSelect) onThemeSelect(emptyTheme);
    }
  };

  const handleStickerSetSelect = (stickerSet) => {
    if (!stickerSet) return;

    setSelectedStickerSet(stickerSet);

    if (localSelectedTheme) {
      const updatedTheme = { ...localSelectedTheme, selectedSticker: stickerSet };
      setLocalSelectedTheme(updatedTheme);
    }

    if (onStickerSetSelect) onStickerSetSelect(stickerSet);
  };

  const handlePrevThemePage = () => {
    if (themePageIndex > 0) setThemePageIndex(themePageIndex - 1);
  };

  const handleNextThemePage = () => {
    if (themePageIndex < getTotalPages() - 1) setThemePageIndex(themePageIndex + 1);
  };

  // Enable mouse wheel (middle wheel) scrolling on the horizontal sticker set/category bar.
  // Convert vertical scroll into horizontal scroll so users don't need to drag the tiny bar.
  useEffect(() => {
    const el = stickerSetsContainerRef.current;
    if (!el) return;

    const onWheel = (e) => {
      try {
        const target = stickerSetsContainerRef.current;
        if (!target) return;

        const dy = e.deltaY || 0;
        const dx = e.deltaX || 0;

        // Trackpads often provide horizontal deltas already; don't override those.
        if (Math.abs(dx) > Math.abs(dy)) return;
        if (dy === 0) return;

        const canScroll = target.scrollWidth > target.clientWidth;
        if (!canScroll) return;

        target.scrollLeft += dy;
        e.preventDefault();
      } catch {}
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      try { el.removeEventListener('wheel', onWheel); } catch {}
    };
  }, [localSelectedTheme]);

  // Auto select theme on mount or when selectedTheme prop changes
  useEffect(() => {
    if (!selectedTheme) {
      if (!hasAutoSelected && displayThemes.length > 0) {
        handleThemeSelect(displayThemes[0]);
        setHasAutoSelected(true);
      }
      return;
    }
  
    // Only update local state if selectedTheme has changed
    const selectedThemeChanged = !localSelectedTheme || 
        (selectedTheme.ThemeName !== localSelectedTheme.ThemeName);
  
    if (selectedThemeChanged) {
      setLocalSelectedTheme(selectedTheme);
  
      if (selectedTheme.stickerSets && selectedTheme.stickerSets.length > 0) {
        setSelectedStickerSet(selectedTheme.selectedSticker || selectedTheme.stickerSets[0]);
      } else if (selectedTheme.StickersCsv && selectedTheme.StickersCsv.length > 0) {
        setSelectedStickerSet(selectedTheme.selectedSticker || selectedTheme.StickersCsv[0]);
      } else {
        setSelectedStickerSet(null);
      }
  
      const idx = displayThemes.findIndex(t => (t.ThemeName === selectedTheme.ThemeName));
      if (idx !== -1) {
        setThemePageIndex(Math.floor(idx / THEMES_PER_PAGE));
      }
  
      setHasAutoSelected(true);
    }
  }, [selectedTheme, displayThemes]); // Removed hasAutoSelected and localSelectedTheme from deps
  

  if (displayThemes.length === 0) {
    return null;
  }

  return (
    <div className="sticker-tab-theme-selector">
      {allowThemeSelection && (
        <div className="sticker-theme-carousel" role="list">
          <button
            className="sticker-theme-nav-arrow prev"
            onClick={handlePrevThemePage}
            disabled={themePageIndex === 0}
            aria-label={t('previousPage')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <div className="sticker-theme-cards">
            {getCurrentPageThemes().map((themeItem, index) => {
              const globalIndex = themePageIndex * THEMES_PER_PAGE + index;
              const isSelected = (localSelectedTheme && localSelectedTheme.ThemeName === themeItem.ThemeName) ||
                                (selectedTheme && selectedTheme.ThemeName === themeItem.ThemeName);

              return (
                <div
                  key={themeItem.id || themeItem.ThemeName || `theme_${globalIndex}`}
                  className={`sticker-theme-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleThemeSelect(themeItem)}
                  tabIndex={0}
                  role="button"
                  aria-pressed={isSelected}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleThemeSelect(themeItem);
                    }
                  }}
                  aria-label={themeItem.ThemeName}
                >
                  <div className="sticker-theme-name">
                    {themeItem.ThemeName || `Theme ${globalIndex + 1}`}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            className="sticker-theme-nav-arrow next"
            onClick={handleNextThemePage}
            disabled={themePageIndex >= getTotalPages() - 1}
            aria-label={t('nextPage')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      )}

      {/* Sticker set selection always visible if sticker sets available */}
      {localSelectedTheme && (localSelectedTheme.stickerSets || localSelectedTheme.StickersCsv) && (
        <div className="sticker-sets-selection" role="list">
          <div className="sticker-sets-carousel">
            {/* Only show navigation arrows if there's more than one sticker set */}
            {((localSelectedTheme.stickerSets?.length ?? 0) > 1 || (localSelectedTheme.StickersCsv?.length ?? 0) > 1) && (
              <button
                className="sticker-set-nav-arrow prev"
                onClick={() => {
                  const container = stickerSetsContainerRef.current;
                  if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
                }}
                aria-label={t('previousStickerSets')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}
            
            {/* Only show sticker sets container if there's more than one option */}
            {((localSelectedTheme.stickerSets?.length ?? 0) > 1 || (localSelectedTheme.StickersCsv?.length ?? 0) > 1) && (
              <div className="sticker-sets-button-container" ref={stickerSetsContainerRef}>
                {(localSelectedTheme.stickerSets || localSelectedTheme.StickersCsv).map((stickerSet, index) => {
                  // Support both legacy stickerSets array or direct StickersCsv
                  const stickerName = stickerSet.StickerName || stickerSet.Name;
                  const currentSelected = selectedStickerSet || (localSelectedTheme && localSelectedTheme.selectedSticker);
                  const isSelected = currentSelected && ((currentSelected.StickerName === stickerName) || (currentSelected.Name === stickerName));

                  return (
                    <button
                      key={index}
                      className={`sticker-set-button ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleStickerSetSelect(stickerSet)}
                      aria-pressed={isSelected}
                      aria-label={stickerName}
                    >
                      {stickerName}
                    </button>
                  );
                })}
                {((localSelectedTheme.stickerSets?.length ?? 0) + (localSelectedTheme.StickersCsv?.length ?? 0)) === 0 && (
                  <div>{t('stickers.noStickerSetsAvailable')}</div>
                )}
              </div>
            )}
            
            {/* Only show navigation arrows if there's more than one sticker set */}
            {((localSelectedTheme.stickerSets?.length ?? 0) > 1 || (localSelectedTheme.StickersCsv?.length ?? 0) > 1) && (
              <button
                className="sticker-set-nav-arrow next"
                onClick={() => {
                  const container = stickerSetsContainerRef.current;
                  if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
                }}
                aria-label={t('nextStickerSets')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StickerTabThemeSelector;
